import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useParams, useHistory } from "react-router-dom";

import { Grid, Typography } from "@mui/material";
import LockOpen from "@mui/icons-material/LockOpen";
import Password from "@mui/icons-material/Password";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import {
	LoadingBar,
	BreadCrumbs,
	PrimaryButton,
	DeleteModal,
	ChangePasswordModal,
	Tabs,
	ActionBox
} from "../Common";
import AssignedLocations from "./AssignedLocations";
import { DetailPanel, DetailPanelData } from "../Common/DetailPanel";
import { Pill } from "../Common/Pill";
import Table from "../Common/Table";
import Column from "../Common/Table/Column";

import Routes from "../../routes";
import UserInfo from "../../UserInfo";
import Enums from "../../enums";
import { PublishedWithChanges } from "../../Icons/Icons";
import { trakrefFontFamily, AppLanguage, isGeekCommandCenterUserContext } from "../../utils";
import { Grey1, toolTipBlue } from "../../color_utils";
import { TranslateW } from "../../Utils/Languages/Translate";
import useNavigate from "../../hooks/useNavigate";
import useBreadCrumbs from "../../hooks/useBreadCrumbs";

const initialState = {
	id: 0,
	guid: "",
	name: "",
	firstName: "",
	lastName: "",
	userName: "",
	email: "",
	state: null,
	stateName: null,
	isReseller: false,
	country: "",
	requireAdminApproval: false,
	certificationType: "0",
	language: "en-US",
	userType: 1,
	userTypeName: "",
	hasOtherInstances: false,
	notificationTypeId: 1,
	weightUnits: "",
	userPermissions: [],
	company: "",
	address1: "",
	address2: "",
	county: "",
	countryName: "",
	title: "",
	city: "",
	zip: "",
	phone: "",
	cell: "",
	fax: "",
	shortName: "",
	fkid: 0,
	fkName: "",
	headshot: "",
	deactivateDate: null
};

const User = () => {
	const [userInformation, setUserInformation] = useState(initialState);
	const [loading, setLoading] = useState(true);
	const [refresh, setRefresh] = useState(false);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showDeactivateModal, setShowDeactivateModal] = useState(false);
	const [showReactivateModal, setShowReactivateModal] = useState(false);
	const [activeTab, setActiveTab] = useState(1);
	const [actionBoxItems, setActionBoxItems] = useState([]);

	const { id } = useParams();
	const history = useHistory();
	// US 16905: no location-access editing from the Geek Command Center path
	const hideAssignedLocations = isGeekCommandCenterUserContext();
	const navigate = useNavigate({
		pageName: 'User'
	});
	const breadCrumbs = useBreadCrumbs({
		path: { id: 'User', text: userInformation.name, link: window.location.pathname }
	});

	const onhandleChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	const onPasswordModalState = () => {
		setShowPasswordModal((prev) => !prev);
	};

	const onDeleteUser = async () => {
		try {
			const response = await fetch(Routes.User.DeleteUser(id), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
			});
			const data = await response.json();

			if (response.status !== 200 || !data) {
				return;
			}

			navigate.goBack("/Users");
			setShowDeleteModal(false);
			toast.success('The user has been successfully deleted', { duration: 6000 });
		} catch {
			console.error("something went wrong!");
		}
	};

	const onDeactivateUser = async () => {
		try {
			const response = await fetch(Routes.User.DeactivateUser(id), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
			});
			const data = await response.json();

			if (response.status !== 200 || !data) {
				return;
			}

			setUserInformation(prev => ({
				...prev,
				deactivateDate: new Date().getUTCDate()
			}));
			setShowDeactivateModal(false);
			setRefresh(!refresh);
			toast.success('The user has been successfully deactivated', { duration: 6000 });
		} catch {
			console.error("something went wrong!");
		}
	};

	const onReactivateUser = async () => {
		try {
			const response = await fetch(Routes.User.ReactivateUser(id), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
			});
			const data = await response.json();

			if (response.status !== 200 || !data) {
				return;
			}

			setUserInformation(prev => ({
				...prev,
				deactivateDate: null
			}));
			setShowReactivateModal(false);
			setRefresh(!refresh);
			toast.success('The user has been successfully reactivated', { duration: 6000 });
		} catch {
			console.error("something went wrong!");
		}
	};

	const onNavigateEdit = () => {
		navigate.push(`/UserUpdate/${id}`);
	};

	const fetchUserData = useCallback(async () => {
		const response = await fetch(Routes.User.GetUserDetails(id));
		const user = await response.json();

		if (response.status === 404) {
			history.push("/Error404");
			return;
		}

		const newUser = {
			...user,
			language: AppLanguage.find((x) => x.id === user.language).name,
		};

		setUserInformation({ ...userInformation, ...newUser });
		setLoading(false);
	}, [id]);

	useEffect(() => {
		fetchUserData();
	}, [fetchUserData]);

	useEffect(() => {
		
		if (userInformation.id <= 0) {
			return;
		}

		const items = [];

		if (userInformation.userStatus !== "Deleted") {
			if (userInformation.canActivateSSO) {
				items.push({
					Icon: LockOpen,
					title: userInformation.loginType === Enums.LoginType.Trakref ? "Activate SSO" : "Deactivate SSO",
					onClick: () => {
						if (userInformation.loginType === Enums.LoginType.Trakref) {
							ActivateSSO();
						} else {
							DeactivateSSO();
						}
					}
				});
			}

			if (userInformation.canPasswordUpdate && userInformation.loginType === Enums.LoginType.Trakref && UserInfo.getLoginTypeId() === Enums.LoginType.Trakref) {
				items.push({
					Icon: Password,
					title: "Change Password",
					onClick: onPasswordModalState
				});
			}
			
			if (userInformation.canDeactivateUser) {
				items.push({
					Icon: userInformation.deactivateDate ? PublishedWithChanges : CloseOutlinedIcon,
					title: userInformation.deactivateDate ? "Reactivate User" : "Deactivate User",
					onClick: () => {
						if (userInformation.deactivateDate) {
							setShowReactivateModal(true);
						} else {
							setShowDeactivateModal(true)
						}
					}
				});
			}

			if (userInformation.canRemoveUser) {
				items.push({
					Icon: DeleteOutlineOutlinedIcon,
					title: "Delete User",
					onClick: () => setShowDeleteModal(true)
				});
			}
		}

		setActionBoxItems(items);
		breadCrumbs.updateBreadCrumbs({ id: 'User', text: userInformation.name, link: window.location.pathname });
	}, [userInformation.id, refresh])

	if (loading && userInformation.id === 0) {
		return <LoadingBar />;
	}

	const ActivateSSO = async () => {
		try {
			const response = await fetch(Routes.User.ActivateSSO(id), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
			});
			const data = await response.json();

			if (response.status !== 200 || !data) {
				return;
			}

			setUserInformation(prev => ({
				...prev,
				loginType: Enums.LoginType.SSO
			}));
			setRefresh(!refresh);
			toast.success('SSO was successfully activated for ' + userInformation.firstName + ' ' + userInformation.lastName, { duration: 6000 });
		} catch {
			console.error("something went wrong!");
		}
	}

	const DeactivateSSO = async () => {
		try {
			const response = await fetch(Routes.User.DeactivateSSO(id), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
			});
			const data = await response.json();

			if (response.status !== 200 || !data) {
				return;
			}

			setUserInformation(prev => ({
				...prev,
				loginType: Enums.LoginType.Trakref
			}));
			setRefresh(!refresh);
			toast.success('SSO was successfully deactivated for ' + userInformation.firstName + ' ' + userInformation.lastName, { duration: 6000 });
		} catch {
			console.error("something went wrong!");
		}
	}

	return (
		<>
			<BreadCrumbs
				path={breadCrumbs.getSortedBreadCrumbs()}
			/>

			<Grid container direction="row" alignItems="center" spacing={1}>
				<Grid item ml={"14px"} mr={"10px"} xs={12} md={12}>
					<div className="profile-header-container">
						<div className="profile-name-container">
							{userInformation.headshot ? (
								<img
									className="greenImageSmall"
									src={userInformation.headshot}
									alt="headshot"
								/>
							) : (
								<div className="greenCircleSmall">
									{userInformation.shortName}
								</div>
							)}
							<Typography
								variant="h5"
								sx={{
									"&.MuiTypography-root": {
										fontFamily: trakrefFontFamily,
										fontSize: "22px",
									},
								}}
								style={{ color: Grey1 }}
							>
								{userInformation.name}
							</Typography>
							<Pill text={userInformation.userTypeName} bgColor={toolTipBlue} />
						</div>
						{
							(userInformation.userStatus !== "Deleted" && userInformation.canEditUser) && (
								<PrimaryButton
									buttonText={TranslateW("Profile.Edit")}
									onClick={onNavigateEdit}
								/>
							)
						}
					</div>
				</Grid>
			</Grid>

			<Grid container marginTop={"0.4rem"} spacing={1}>
				<Grid item sm={10} md={10} lg={10}>
					<DetailPanel expandable={true}>
						{!UserInfo.IsWriteIntech && (
							<DetailPanelData
								title={TranslateW("Profile.Username")}
								data={userInformation.userName}
								link={false}
							/>
						)}
						{!UserInfo.IsWriteIntech && (
							<DetailPanelData
								title={TranslateW("Profile.EmailAddress")}
								data={userInformation.email}
								link={false}
							/>
						)}
						<DetailPanelData
							title={TranslateW("Profile.TechnicianCertificationNumber")}
							data={""}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Firstname")}
							data={userInformation.firstName}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Company")}
							data={userInformation.fkName}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Address1")}
							data={userInformation.address1}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Lastname")}
							data={userInformation.lastName}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Title")}
							data={userInformation.title}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.City")}
							data={userInformation.city}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Address2")}
							data={userInformation.address2}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.County")}
							data={userInformation.county}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Country")}
							data={userInformation.countryName}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.State")}
							data={userInformation.stateName || userInformation.state}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Zip")}
							data={userInformation.zip}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Phone")}
							data={userInformation.phone}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Cell")}
							data={userInformation.cell}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Fax")}
							data={userInformation.fax}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Language")}
							data={userInformation.language}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Weightsin")}
							data={userInformation.weightUnits}
							link={false}
						/>
						<DetailPanelData
							title={TranslateW("Profile.Role")}
							data={userInformation.userTypeName}
							link={false}
						/>
					</DetailPanel>
				</Grid>
				<Grid item sm={2} md={2} lg={2}>
					{
						!!actionBoxItems.length && (
							<ActionBox
								items={actionBoxItems}
							/>
						)
					}
				</Grid>
				<Grid item sm={12} md={12} lg={12}>
					<Tabs
						activeTab={activeTab}
						onhandleChange={onhandleChange}
						labels={[
							TranslateW("Profile.ServiceEvents"),
							...(hideAssignedLocations ? [] : [TranslateW("Profile.AssignedLocations")])
						]}
						tabElements={[
							<Table 
								url={Routes.ServiceEvents.ByUserProfile(id)}
								noResultText="Oops...It is empty here..."
								noResultText2="Please create service event to get started."	
							>
								<Column
									title="SERVICE EVENT ID"
									property="id"
									link="/ServiceEvent/"
									linkId="id"
								/>
								<Column title="CITY" property="city" />
								<Column title="WORK ORDER ID" property="workOrderId" />
								<Column title="CUSTOMER WORK #" property="workOrderNumber" />
								<Column title="TYPE" property="type" />
								<Column title="SERVICE DATE" property="serviceDate" />
								<Column title="PRIMARY TECH" property="primaryTechnicianName" />
								<Column title="ASSET NAME" property="assetName" />
								<Column title="NET GAS ADDED" property="netGasAdded" />
								<Column
									title="PER OCCURRANCE LEAK RATE %"
									property="leakRate"
									type="percentage"
								/>
							</Table>,
							...(hideAssignedLocations ? [] : [
								<AssignedLocations
									userInformation={userInformation}
									navigate={navigate}
								/>
							])
						]}
						tabCustomStyle={{ ml: "25px" }}
						tabPanelCustomStyle={{ ml: "1px" }}
					/>
				</Grid>
			</Grid>

			<DeleteModal
				open={showDeleteModal}
				buttonText={TranslateW("User.YesDelete")}
				title={TranslateW("User.DeleteUser")}
				description={TranslateW("User.DeleteUserWarning")}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={onDeleteUser}
			/>

			<DeleteModal
				open={showDeactivateModal}
				buttonText={TranslateW("User.YesDeactivate")}
				title={TranslateW("User.DeactivateUser")}
				description={TranslateW("User.DeactivateUserWarning")}
				onClose={() => setShowDeactivateModal(false)}
				onConfirm={onDeactivateUser}
			/>

			<DeleteModal
				open={showReactivateModal}
				buttonText={TranslateW("User.YesReactivate")}
				title={TranslateW("User.ReactivateUser")}
				description={TranslateW("User.ReactivateUserWarning")}
				onClose={() => setShowReactivateModal(false)}
				onConfirm={onReactivateUser}
			/>

			<ChangePasswordModal
				modalOpen={showPasswordModal}
				userId={id}
				onClose={onPasswordModalState}
				onLogoutSuccess={onPasswordModalState}
				isProfile={false}
			/>
		</>
	);
};

export default User;
